import {
  Criteria,
  PaginatedAPIResponse,
  SelectInputType,
} from '@order-management/types';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Columns, Element, Form } from 'react-bulma-components';
import { CgSpinner } from 'react-icons/cg';
import Checkbox from '../../../../ui/checkbox/checkbox';
import FeedbackMessage from '../../../../ui/feedback-message/feedback-message';
import InfiniteScroll from '../../../../ui/infinite-scroll/infinite-scroll';
import Conditional from '../../../conditional/conditional';
import useHttpRequest from '../../../hooks/http-request';
import styles from './select-checklist-input.module.scss';

export type SelectInputProps<T, K> = {
  name: string;
  localData: T;
  setLocalData: Dispatch<SetStateAction<T>>;
  onValidateField?: (fieldName: string, valid: boolean) => void;
  errorText?: string;
  input: SelectInputType<T, K>;
};

const initialCriteria: Criteria = {
  itemsPerPage: 50,
  pageNumber: 1,
  search: '',
  sortBy: '',
};

export function SelectChecklistInput<T, K>({
  input: { valueKey, options = [], fetchItems, resolveOption },
  localData,
  setLocalData,
  errorText,
}: SelectInputProps<T, K>) {
  const [criteria, setCriteria] = useState<Criteria>(initialCriteria);
  const [items, setItems] = useState<K[]>([]);
  const [itemsResponse, setItemsResponse] = useState<PaginatedAPIResponse<K>>();
  const { executeHttpRequest, isLoading } = useHttpRequest();

  const inputOptions = useMemo(() => {
    let data = options;
    if (fetchItems && resolveOption) {
      data = data.concat(items.map((item) => resolveOption(item)));
    }
    return data;
  }, [options, fetchItems, items, resolveOption]);

  const handleOptionClick = useCallback(
    (value: string) => {
      let currentValues = localData[valueKey] as string[] | undefined;
      if (!currentValues) {
        currentValues = [];
      }

      const isSelected = currentValues.includes(value);
      const newValues = isSelected
        ? currentValues.filter((v) => v !== value)
        : [...currentValues, value];

      setLocalData({
        ...localData,
        [valueKey]: newValues,
      });
    },
    [localData, setLocalData, valueKey]
  );

  const fetchMoreItems = useCallback(() => {
    if (!isLoading && fetchItems) {
      executeHttpRequest({
        asyncFunction: async () => {
          const nextPage = criteria.pageNumber + 1;
          const updatedCriteria = {
            ...criteria,
            pageNumber: nextPage,
          };
          const newItemsResponse = await fetchItems(updatedCriteria);
          setCriteria(updatedCriteria);
          setItems((prevItems) => [...prevItems, ...newItemsResponse.data]);
          setItemsResponse(newItemsResponse);
        },
      });
    }
  }, [criteria, executeHttpRequest, fetchItems, isLoading]);

  // Down on the last option waits for the next page rather than wrapping
  // around to the first one, and focuses the first new option once it loads.
  const [focusIndex, setFocusIndex] = useState<number>();

  const handleOptionKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>, index: number) => {
      const isLast = index === inputOptions.length - 1;
      if (e.key !== 'ArrowDown' || !isLast || !itemsResponse?.hasMore) return;

      e.preventDefault();
      e.stopPropagation();
      // Brings the loading indicator below the options into view.
      const list = e.currentTarget.parentElement;
      if (list) list.scrollTop = list.scrollHeight;
      setFocusIndex(index + 1);
      fetchMoreItems();
    },
    [fetchMoreItems, inputOptions.length, itemsResponse?.hasMore]
  );

  useEffect(() => {
    if (fetchItems && !itemsResponse) {
      executeHttpRequest({
        asyncFunction: async () => {
          const response = await fetchItems(initialCriteria);
          setItems(response.data);
          setItemsResponse(response);
        },
      });
    }
  }, [fetchItems, executeHttpRequest, itemsResponse]);

  return (
    <Element className={styles.container}>
      <Conditional condition={!itemsResponse && !options}>
        <Conditional.True>
          <Element display="flex" justifyContent="center" alignItems="center">
            <CgSpinner className="animate-spin" />
          </Element>
        </Conditional.True>
        <Conditional.False>
          <Columns marginless display="flex">
            <Columns.Column paddingless>
              <Form.Field id="type" alignItems="center">
                <Form.Control>
                  <InfiniteScroll
                    fetchMore={fetchMoreItems}
                    hasMore={!!itemsResponse?.hasMore}
                    className={styles.scrollContainer}
                  >
                    {inputOptions.map((option, index) => {
                      const isSelected = Array.isArray(localData[valueKey])
                        ? (localData[valueKey] as string[]).includes(
                            option.value
                          )
                        : localData[valueKey] === option.value;

                      return (
                        <div
                          key={`${option.label}-${index}`}
                          className={styles.option}
                          onClick={() => handleOptionClick(option.value)}
                          onKeyDown={(e) => handleOptionKeyDown(e, index)}
                        >
                          <Checkbox
                            className={styles.checkbox}
                            checked={isSelected}
                            aria-label={option.label}
                            autoFocus={index === focusIndex}
                            readOnly
                          />
                          <span className="is-size-6 ml-2">{option.label}</span>
                        </div>
                      );
                    })}
                  </InfiniteScroll>
                </Form.Control>
              </Form.Field>
            </Columns.Column>
          </Columns>
        </Conditional.False>
      </Conditional>
      <span role="status" className="is-sr-only">
        {isLoading && itemsResponse ? 'Loading more options' : ''}
      </span>
      <FeedbackMessage>{errorText}</FeedbackMessage>
    </Element>
  );
}

export default SelectChecklistInput;
